/**
 * Heuristic helpers that turn a freshly-picked RxNorm display string (e.g.
 * "Acetaminophen 325 MG Oral Tablet", "Lisinopril 10 MG Oral Tablet",
 * "Atorvastatin 20 MG Oral Tablet [Lipitor]") into auto-fill values for the
 * medication row's `dose` and `route` fields. Frequency and status are
 * intentionally NOT inferred — those are clinical decisions the doctor
 * makes per-prescription, not properties of the RxNorm concept.
 *
 * Why string parsing rather than RxNav lookups: the picker's payload is the
 * `ValueSet.expand` result, which carries only `{code, display, system}`.
 * Doing a second hop to RxNav for each pick would double-bounce the
 * typeahead. The display string already encodes strength and dose form by
 * convention in SCD ("Semantic Clinical Drug") concepts, so a focused
 * regex pass covers the realistic dev/prod cases without an extra fetch.
 */

/**
 * Route option exposed in the medication row's Route dropdown. The `value`
 * field is what we persist on `MedicationStatement.dosage[0].route.text` /
 * the form's `route` string column — kept human-readable (e.g. "PO",
 * "Topical") rather than full SNOMED codes, so coders writing claims and
 * doctors reading the chart see the same shorthand they would on a paper
 * Rx pad. SNOMED-coded route mapping is a follow-up if/when downstream
 * billing requires it.
 */
export type RouteOption = {
  value: string
  label: string
}

export const ROUTE_OPTIONS: RouteOption[] = [
  { value: 'PO', label: 'PO — by mouth' },
  { value: 'SL', label: 'SL — sublingual' },
  { value: 'Buccal', label: 'Buccal' },
  { value: 'IV', label: 'IV — intravenous' },
  { value: 'IM', label: 'IM — intramuscular' },
  { value: 'SC', label: 'SC — subcutaneous' },
  { value: 'Topical', label: 'Topical' },
  { value: 'Transdermal', label: 'Transdermal' },
  { value: 'Inhaled', label: 'Inhaled' },
  { value: 'Nasal', label: 'Nasal' },
  { value: 'Ophthalmic', label: 'Ophthalmic' },
  { value: 'Otic', label: 'Otic' },
  { value: 'PR', label: 'PR — rectal' },
  { value: 'PV', label: 'PV — vaginal' },
]

const STRENGTH_REGEX =
  // numbers (incl. decimal) followed by an RxNorm-canonical unit. Allow
  // composite expressions like "5-325 MG" (combo drugs) and "0.5 mg/mL"
  // (concentrations). `\b` anchors keep us from matching "325 MGOral".
  /\b\d+(?:\.\d+)?(?:[-/]\d+(?:\.\d+)?)*\s*(?:mg\/mL|mcg\/mL|mcg|mg|g|mL|units?|unt|iu|%)\b/i

/**
 * Extract the strength portion of an RxNorm display. Returns the matched
 * substring (e.g. "325 MG", "10 MG", "5-325 MG", "0.5 mg/mL") or `null` if
 * the display doesn't carry a recognisable strength.
 *
 * Caller is expected to NOT overwrite an existing `dose` value — the AI
 * scribe or the doctor may have already typed something more clinically
 * meaningful ("2 tablets PO daily") and we don't want to clobber it.
 */
export function extractDoseFromRxNorm(display: string | undefined | null): string | null {
  if (!display) return null
  const m = display.match(STRENGTH_REGEX)
  return m ? m[0].trim() : null
}

/**
 * Detect the most likely route from an RxNorm display's dose form. The
 * match returned is one of `ROUTE_OPTIONS.value` so the caller can
 * preselect the corresponding dropdown entry directly. Returns `null`
 * when no recognised dose form appears in the string.
 *
 * Ambiguities resolve to the most common route — e.g. an "Injectable
 * Solution" gets mapped to `IM` because the typical primary-care visit
 * (vaccines, depot injections) is IM. The doctor can switch to IV/SC via
 * the dropdown if needed.
 */
export function extractRouteFromRxNorm(display: string | undefined | null): string | null {
  if (!display) return null
  const lower = display.toLowerCase()

  // Order matters: more specific phrases first so "Oral Tablet" beats a
  // bare "Tablet" fallback, and "Eye Drops" beats "Drops".
  if (/\bsublingual\b/.test(lower)) return 'SL'
  if (/\bbuccal\b/.test(lower)) return 'Buccal'
  if (/\btransdermal|patch\b/.test(lower)) return 'Transdermal'
  if (/\bophthalmic|eye drops?\b/.test(lower)) return 'Ophthalmic'
  if (/\botic|ear drops?\b/.test(lower)) return 'Otic'
  if (/\bnasal|nose spray\b/.test(lower)) return 'Nasal'
  if (/\binhal(?:e|ation|er)|nebuliz|metered dose|mdi\b/.test(lower)) return 'Inhaled'
  if (/\brectal|suppository\b/.test(lower)) return 'PR'
  if (/\bvaginal|pessary\b/.test(lower)) return 'PV'
  if (/\bsubcutaneous|sub-?cut|\bsc\b/.test(lower)) return 'SC'
  if (/\bintravenous|\biv\b/.test(lower)) return 'IV'
  if (/\bintramuscular|\bim\b/.test(lower)) return 'IM'
  // "Injectable Solution" / "Injection" with no other hint → fall back to
  // IM as the primary-care default. IV/SC switchable via dropdown.
  if (/\binjectable|injection\b/.test(lower)) return 'IM'
  if (/\btopical|cream|ointment|lotion|gel\b/.test(lower)) return 'Topical'
  // Default oral form keywords last — they're the broadest match.
  if (/\boral|tablet|capsule|chewable|lozenge|syrup|elixir|suspension|po\b/.test(lower)) {
    return 'PO'
  }
  return null
}
